import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

// ─── Brand Logo ────────────────────────────────────────────────────────────────
const SoulSathiyaLogo = ({ className = 'h-[38px] w-auto' }) => (
  <img
    src="/logo-lockup.svg"
    alt="SoulSathiya"
    className={`${className} object-contain`}
    draggable={false}
  />
);

// ─── Config ────────────────────────────────────────────────────────────────────
export const SOCIAL_LINKS = [
  { icon: Facebook,  href: 'https://www.facebook.com/profile.php?id=61569091466613',  label: 'Facebook' },
  { icon: Twitter,   href: 'https://x.com/soul_sathiya',   label: 'Twitter / X' },
  { icon: Instagram, href: 'https://instagram.com/soulsathiya', label: 'Instagram' },
  { icon: Linkedin,  href: 'https://www.linkedin.com/company/soulsathiya/', label: 'LinkedIn' },
];

// Live on Google Play since 29 Oct 2026 (India). Badge artwork is Google's own, used per
// its badge guidelines; the link opens the store listing.
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.soulsathiya.app';
const PLAY_BADGE_SRC = 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';

const FOOTER_COLS = [
  {
    heading: 'Product',
    links: [
      { label: 'Intentional Matchmaking',  internal: true, to: '/intentional-matchmaking'  },
      { label: 'Relationship Intelligence', internal: true, to: '/relationship-intelligence' },
      { label: 'Relationship Wellness',    internal: true, to: '/care'                     },
      { label: 'Relationship Ecosystem',   internal: true, to: '/ecosystem'                },
    ],
  },
  {
    heading: 'Support',
    links: [
      { label: 'Help Center',  internal: true, to: '/help'        },
      { label: 'Safety Tips',  internal: true, to: '/safety-tips' },
      { label: 'Contact Us',   internal: true, to: '/contact'     },
      { label: 'FAQ',          internal: true, to: '/faq'         },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'How it works',     internal: true,  to:   '/how-it-works' },
      { label: 'About Us',         internal: true,  to:   '/about'        },
      { label: 'Our Method',       internal: true,  to:   '/methodology'  },
      // 'Success Stories' hidden until real stories are approved (decision C, Oct 9 2026).
      { label: 'Pricing/Plans',    internal: true,  to:   '/intentional-matchmaking#pricing' },
      { label: 'For Marriage Bureaus', internal: true, to: '/alliance-members' },
      { label: 'Invite a Friend',  internal: true,  to:   '/refer' },
    ],
  },
];

const LEGAL_LINKS = [
  { label: 'Privacy Policy',        to: '/privacy' },
  { label: 'Terms of Services',     to: '/terms' },
  { label: 'Cancellation & Refunds', to: '/cancellation-policy' },
  { label: 'Cookie Policy',         to: '/privacy' },
];

// ─── Link Item ─────────────────────────────────────────────────────────────────
const FooterLink = ({ link }) => {
  const cls = 'text-base text-primary hover:text-primary/80 transition-colors duration-200';

  if (link.internal) {
    return (
      <Link to={link.to} className={cls}>
        {link.label}
      </Link>
    );
  }
  return (
    <a
      href={link.href}
      className={cls}
      target={link.href.startsWith('http') ? '_blank' : undefined}
      rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
    >
      {link.label}
    </a>
  );
};

// ─── SiteFooter ───────────────────────────────────────────────────────────────
const SiteFooter = () => (
  <footer style={{ background: '#0b1729' }} className="px-6 lg:px-24 py-14">
    <div className="container mx-auto space-y-8">

      {/* ── Top: brand + 3 columns ───────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-10 lg:gap-[70px] items-start">

        {/* Brand column */}
        <div className="space-y-4 flex-1">
          <div className="flex items-center">
            <SoulSathiyaLogo className="h-[38px] w-auto" />
          </div>
          <p className="text-base" style={{ color: '#d9e4f5' }}>
            A deeper way to understand who you're choosing and why.
          </p>
          <div className="flex items-center gap-7 pt-2">
            {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-primary transition-colors duration-200"
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get the SoulSathiya app on Google Play"
            className="inline-block pt-1 opacity-90 hover:opacity-100 transition-opacity duration-200"
          >
            <img
              src={PLAY_BADGE_SRC}
              alt="Get it on Google Play"
              className="h-14 w-auto"
              loading="lazy"
              draggable={false}
            />
          </a>
        </div>

        {/* Link columns */}
        <div className="flex flex-wrap gap-10 sm:gap-[143px]">
          {FOOTER_COLS.map(col => (
            <div key={col.heading} className="space-y-4">
              <h4 className="font-body text-base font-medium" style={{ color: '#d0daf5' }}>
                {col.heading}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map(link => (
                  <li key={link.label}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom Bar: copyright + legal links ──────────────────────── */}
      <div className="h-0.5 w-full" style={{ background: "#152c4f" }} />
      <div className="flex flex-col sm:flex-row items-center justify-end gap-5 sm:gap-11 text-sm" style={{ color: '#d9e4f5' }}>
        <p className="flex-1 w-full">© 2026 SoulSathiya. All rights reserved.</p>
        {LEGAL_LINKS.map(link => (
          <Link key={link.label} to={link.to} className="whitespace-nowrap hover:text-primary transition-colors">
            {link.label}
          </Link>
        ))}
      </div>

    </div>
  </footer>
);

export default SiteFooter;
